import type { components } from '@nutians/api-client';
import { friendlyErrorMessage } from '@nutians/shared';
import { LOCALES } from '@nutians/shared/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../api.js';
import { changePassword, hasPassword, setPassword } from '../auth.js';
import { CustomFoodForm } from '../components/CustomFoodForm.js';
import { PremiumGate } from '../components/PremiumGate.js';
import { usePwaEnvironment } from '../components/pwa/usePwaEnvironment.js';
import { RecipeBuilder } from '../components/RecipeBuilder.js';
import { usePremiumDialog } from '../context/PremiumDialogContext.js';
import { useCustomFoods, useDeleteCustomFood } from '../hooks/useCustomFoods.js';
import { useEntitlement } from '../hooks/useEntitlement.js';
import { useMeasurementSettings, useUpdateMeasurementSettings } from '../hooks/useMeasurements.js';
import { useProgress, useUpdateProgressPreference } from '../hooks/useProgress.js';
import { useReportDownload } from '../hooks/useReports.js';
import { useDeleteSavedMeal, useSavedMeals, useUpdateSavedMeal } from '../hooks/useSavedMeals.js';
import { useTrack } from '../hooks/useTrack.js';
import { setLocale } from '../i18n.js';
import {
  getNotificationPermission,
  grantPushConsent,
  isNotificationPermissionSupported,
  isWonderPushEnabled,
  requestBrowserNotificationPermission,
  revokePushConsent,
} from '../push/wonderpush.js';

/** Human label for the store a subscription was bought through (ADR-0022 §6). */
const STORE_SOURCE_LABEL: Record<string, string> = {
  app_store: 'App Store',
  play_store: 'Google Play',
  stripe: 'Web',
};

type OverrideView = components['schemas']['OverrideView'];
type NotificationPreference = components['schemas']['NotificationPreference'];
type ConsentsResponse = components['schemas']['ConsentsResponse'];
type ConsentPurpose = ConsentsResponse['consents'][number]['purpose'];

const MVP_MACROS = ['protein', 'fibre'] as const;
const PUSH_KINDS = ['weekly_review', 'trial_recap', 'trial_ended'] as const;
const CONSENT_PURPOSES: ConsentPurpose[] = [
  'marketing_email',
  'marketing_push',
  'analytics',
  'cycle',
];

function LanguageSection() {
  const { t, i18n } = useTranslation(['settings', 'common']);

  const patchLocaleMutation = useMutation({
    mutationFn: async (locale: string) => {
      await api.PATCH('/me/profile', { body: { locale } });
    },
  });

  async function handleLocaleChange(locale: string) {
    await setLocale(locale);
    patchLocaleMutation.mutate(locale);
  }

  return (
    <section aria-labelledby="language-heading">
      <h3 id="language-heading" className="text-base font-medium text-ink dark:text-ink mb-3">
        {t('settings:language.title')}
      </h3>
      <div className="rounded-xl border border-husk dark:border-ink/20">
        <div className="flex min-h-[44px] items-center justify-between gap-4 px-4 py-3">
          <label htmlFor="locale-select" className="text-sm text-ink dark:text-ink">
            {t('settings:language.label')}
          </label>
          <select
            id="locale-select"
            value={i18n.language}
            onChange={(e) => void handleLocaleChange(e.target.value)}
            className="rounded border border-husk px-2 py-1 text-sm text-ink dark:border-ink/20 dark:bg-transparent dark:text-ink"
          >
            {LOCALES.map((loc) => (
              <option key={loc} value={loc}>
                {t(`common:locale.${loc}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </section>
  );
}

function OverrideList() {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['overrides'],
    queryFn: async () => {
      const { data, error } = await api.GET('/me/overrides');
      if (error || !data) throw new Error('Failed to load overrides');
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE('/me/overrides/{id}', {
        params: { path: { id } },
      });
      if (error) throw new Error('Failed to delete override');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overrides'] });
    },
  });

  if (isLoading) {
    return (
      <p className="text-sm text-ink-soft dark:text-ink-soft" aria-live="polite">
        {t('common:loading', { ns: 'common' })}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-clay dark:text-clay" role="alert" aria-live="assertive">
        {t('settings:overrides.loadError')}
      </p>
    );
  }

  if (!data || data.overrides.length === 0) {
    return (
      <p className="text-sm text-ink-soft dark:text-ink-soft">{t('settings:overrides.empty')}</p>
    );
  }

  return (
    <ul
      className="divide-y divide-neutral-200 dark:divide-neutral-700"
      aria-label={t('settings:overrides.ariaList')}
    >
      {data.overrides.map((override: OverrideView) => (
        <li key={override.id} className="flex items-center justify-between gap-3 py-3">
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-medium text-ink dark:text-ink truncate">
              &ldquo;{override.alias}&rdquo;
            </span>
            <span className="text-xs text-ink-soft dark:text-ink-soft truncate">
              {override.resolvedFoodName ??
                override.resolvedFoodId ??
                t('settings:overrides.customRecipe')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => deleteMutation.mutate(override.id)}
            disabled={deleteMutation.isPending}
            aria-label={t('settings:overrides.removeAria', { alias: override.alias })}
            className="shrink-0 rounded p-1 text-ink-soft hover:text-clay
                       focus:outline-none focus:ring-2 focus:ring-leaf
                       dark:hover:text-clay disabled:opacity-50
                       min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
              <path d="M5 1h6M2 4h12M3 4l1 10h8L13 4" />
            </svg>
          </button>
        </li>
      ))}
    </ul>
  );
}

function trialDaysLeft(periodEnd: string): number {
  const ms = new Date(periodEnd).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

function PlanSection() {
  const { t } = useTranslation('settings');
  const { data: entitlement, isLoading } = useEntitlement();
  const { openDialog: openPremiumDialog } = usePremiumDialog();

  if (isLoading) {
    return (
      <section id="plan" aria-labelledby="plan-heading">
        <h3 id="plan-heading" className="text-base font-medium text-ink dark:text-ink mb-3">
          {t('settings:plan.title')}
        </h3>
        <p className="text-sm text-ink-soft">{t('common:loading', { ns: 'common' })}</p>
      </section>
    );
  }

  const isPremium = entitlement?.isPremium ?? false;
  const inTrial = entitlement?.inTrial ?? false;
  const daysLeft =
    inTrial && entitlement?.currentPeriodEnd ? trialDaysLeft(entitlement.currentPeriodEnd) : null;
  const sourceLabel = entitlement?.store ? STORE_SOURCE_LABEL[entitlement.store] : null;

  return (
    <section id="plan" aria-labelledby="plan-heading">
      <h3 id="plan-heading" className="text-base font-medium text-ink dark:text-ink mb-1">
        {t('settings:plan.title')}
      </h3>

      {inTrial && (
        <div
          className="mb-3 rounded-xl border border-mist/20 bg-mist/10 px-4 py-3
                        dark:border-mist/20 dark:bg-mist/10"
        >
          <p className="text-sm font-medium text-leaf dark:text-mist">
            {t('settings:plan.trialActive')}
            {daysLeft !== null && t('settings:plan.daysRemaining', { count: daysLeft })}
          </p>
          <p className="mt-0.5 text-xs text-leaf dark:text-mist">{t('settings:plan.trialBody')}</p>
        </div>
      )}

      {!isPremium && !inTrial && (
        <div
          className="mb-3 rounded-xl border border-husk bg-paper px-4 py-3
                        dark:border-ink/20 dark:bg-shell"
        >
          <p className="text-sm font-medium text-ink dark:text-ink">
            {t('settings:plan.liteTitle')}
          </p>
          <p className="mt-0.5 text-xs text-ink-soft dark:text-ink-soft mb-3">
            Unlock the AI coach, weekly reviews, full history, and more.
          </p>
          <button
            type="button"
            onClick={openPremiumDialog}
            className="inline-block rounded-xl bg-leaf px-4 py-2 text-sm font-semibold
                       text-white hover:bg-leaf-deep focus:outline-none focus:ring-2
                       focus:ring-leaf focus:ring-offset-1"
          >
            See plans
          </button>
        </div>
      )}

      {isPremium && !inTrial && (
        <div
          className="rounded-xl border border-leaf/20 bg-leaf/10 px-4 py-3
                        dark:border-leaf/20 dark:bg-leaf/10"
        >
          <p className="text-sm font-medium text-leaf dark:text-leaf">
            Premium{sourceLabel && ` · ${sourceLabel}`}
          </p>
          {entitlement?.currentPeriodEnd && (
            <p className="mt-0.5 text-xs text-leaf dark:text-leaf">
              {entitlement.willRenew
                ? t('settings:plan.renews', { date: new Date(entitlement.currentPeriodEnd) })
                : t('settings:plan.expires', { date: new Date(entitlement.currentPeriodEnd) })}
            </p>
          )}
          {entitlement?.managementUrl && (
            <a
              href={entitlement.managementUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block text-xs text-leaf underline hover:text-leaf-deep
                         dark:text-leaf"
            >
              {t('settings:plan.manage')}
            </a>
          )}
        </div>
      )}

      <p className="mt-2 text-xs text-ink-soft dark:text-ink-soft">
        <Trans
          i18nKey="settings:plan.included"
          components={{
            link: <Link to="/settings#plan" className="underline" />,
          }}
        />
      </p>
    </section>
  );
}

function NotificationsSection() {
  const { t } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();
  const pwaEnv = usePwaEnvironment();
  const track = useTrack();
  const [pushErrorKey, setPushErrorKey] = useState<string | null>(null);
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() =>
    typeof window === 'undefined' ? 'unsupported' : getNotificationPermission(),
  );
  const {
    data: prefs,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const { data, error } = await api.GET('/me/notification-preferences');
      if (error || !data) throw new Error('Failed to load notification preferences');
      return data;
    },
  });
  const { data: consents } = useQuery({
    queryKey: ['consents'],
    queryFn: async () => {
      const { data, error } = await api.GET('/me/consents');
      if (error || !data) throw new Error('Failed to load consent preferences');
      return data;
    },
  });

  const mutation = useMutation({
    mutationFn: async (next: NotificationPreference) => {
      const { data, error } = await api.PUT('/me/notification-preferences', { body: next });
      if (error || !data) throw new Error('Failed to update notification preferences');
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['notification-preferences'], data);
    },
  });
  const pushMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      setPushErrorKey(null);

      if (!enabled) {
        await revokePushConsent();
        const { data, error } = await api.PUT('/me/consents', {
          body: {
            surface: 'settings_web',
            consents: [{ purpose: 'marketing_push', granted: false }],
          },
        });
        if (error || !data) throw new Error('Failed to update consent preference');
        return data;
      }

      if (!pwaEnv?.isStandalone || !isWonderPushEnabled() || !isNotificationPermissionSupported()) {
        throw new Error('unavailable');
      }

      const nextPermission =
        getNotificationPermission() === 'granted'
          ? 'granted'
          : await requestBrowserNotificationPermission();
      setPermission(nextPermission);
      if (nextPermission !== 'granted') {
        throw new Error('permissionDenied');
      }

      const { data, error } = await api.PUT('/me/consents', {
        body: {
          surface: 'settings_web',
          consents: [{ purpose: 'marketing_push', granted: true }],
        },
      });
      if (error || !data) throw new Error('Failed to update consent preference');

      await grantPushConsent();
      await track('push_permission_granted', {
        platform: pwaEnv.isIos ? 'ios' : pwaEnv.isAndroid ? 'android' : 'desktop',
        browserKind: pwaEnv.browserKind,
        standalone: pwaEnv.isStandalone,
      });
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['consents'], data);
    },
    onError: (error) => {
      const key =
        error instanceof Error && error.message === 'permissionDenied'
          ? 'permissionDenied'
          : error instanceof Error && error.message === 'unavailable'
            ? 'unavailable'
            : 'saveError';
      setPushErrorKey(key);
      setPermission(getNotificationPermission());
    },
  });

  function update(next: NotificationPreference) {
    mutation.mutate(next);
  }

  function toggleMacro(macro: string, currentlyMuted: boolean) {
    if (!prefs) return;
    const mutedMacros = currentlyMuted
      ? prefs.mutedMacros.filter((m) => m !== macro)
      : [...prefs.mutedMacros, macro];
    update({ ...prefs, mutedMacros });
  }

  function toggleKind(kind: (typeof PUSH_KINDS)[number], currentlyMuted: boolean) {
    if (!prefs) return;
    const mutedKinds = currentlyMuted
      ? prefs.mutedKinds.filter((k) => k !== kind)
      : [...prefs.mutedKinds, kind];
    update({ ...prefs, mutedKinds });
  }

  const pushConsentGranted =
    consents?.consents.find((item) => item.purpose === 'marketing_push')?.granted ?? false;
  const browserPushAvailable =
    Boolean(pwaEnv?.isStandalone) && isWonderPushEnabled() && isNotificationPermissionSupported();
  const pushToggleDisabled =
    pushMutation.isPending || (!pushConsentGranted && !browserPushAvailable);

  return (
    <section aria-labelledby="notifications-heading">
      <h3 id="notifications-heading" className="text-base font-medium text-ink dark:text-ink mb-3">
        {t('settings:notifications.title')}
      </h3>

      {isLoading && (
        <p className="text-sm text-ink-soft">{t('common:loading', { ns: 'common' })}</p>
      )}
      {isError && (
        <p className="text-sm text-clay dark:text-clay" role="alert">
          {t('settings:notifications.loadError')}
        </p>
      )}
      {prefs && (
        <div className="divide-y divide-neutral-200 rounded-lg border border-husk dark:divide-neutral-700 dark:border-ink/20">
          <ToggleRow
            label={t('settings:notifications.enable')}
            checked={!prefs.globalMuted}
            disabled={mutation.isPending}
            onChange={(checked) => update({ ...prefs, globalMuted: !checked })}
          />

          {!prefs.globalMuted &&
            MVP_MACROS.map((macro) => {
              const muted = prefs.mutedMacros.includes(macro);
              return (
                <ToggleRow
                  key={macro}
                  label={t('settings:notifications.macroAlerts', {
                    macro: t(`common:macro.${macro}`, { ns: 'common' }),
                  })}
                  checked={!muted}
                  disabled={mutation.isPending}
                  onChange={() => toggleMacro(macro, muted)}
                />
              );
            })}

          {!prefs.globalMuted &&
            PUSH_KINDS.map((kind) => {
              const muted = prefs.mutedKinds.includes(kind);
              return (
                <ToggleRow
                  key={kind}
                  label={t(`settings:notifications.kind.${kind}`)}
                  checked={!muted}
                  disabled={mutation.isPending}
                  onChange={() => toggleKind(kind, muted)}
                />
              );
            })}

          {!prefs.globalMuted && (
            <div className="px-4 py-3">
              <ToggleRow
                label={t('settings:notifications.pwaPush')}
                checked={pushConsentGranted && permission === 'granted'}
                disabled={pushToggleDisabled}
                onChange={(checked) => pushMutation.mutate(checked)}
              />
              <p className="mt-1 text-xs text-ink-soft dark:text-ink-soft">
                {browserPushAvailable
                  ? t('settings:notifications.pwaPushBody')
                  : t('settings:notifications.pwaPushInstallFirst')}
              </p>
              {pushErrorKey && (
                <p className="mt-2 text-xs text-clay dark:text-clay" role="alert">
                  {t(`settings:notifications.pwaPushError.${pushErrorKey}`)}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function PrivacySection() {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const {
    data: consents,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['consents'],
    queryFn: async () => {
      const { data, error } = await api.GET('/me/consents');
      if (error || !data) throw new Error('Failed to load consent preferences');
      return data;
    },
  });

  const mutation = useMutation({
    mutationFn: async (input: { purpose: ConsentPurpose; granted: boolean }) => {
      const { data, error } = await api.PUT('/me/consents', {
        body: {
          surface: 'settings_web',
          consents: [{ purpose: input.purpose, granted: input.granted }],
        },
      });
      if (error || !data) throw new Error('Failed to update consent preference');
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['consents'], data);
    },
  });

  const byPurpose = new Map(consents?.consents.map((item) => [item.purpose, item]));

  return (
    <section aria-labelledby="privacy-heading">
      <h3 id="privacy-heading" className="text-base font-medium text-ink dark:text-ink mb-3">
        {t('settings:privacy.title')}
      </h3>

      {isLoading && (
        <p className="text-sm text-ink-soft">{t('common:loading', { ns: 'common' })}</p>
      )}
      {isError && (
        <p className="text-sm text-clay dark:text-clay" role="alert">
          {t('settings:privacy.loadError')}
        </p>
      )}
      {consents && (
        <div className="divide-y divide-neutral-200 rounded-lg border border-husk dark:divide-neutral-700 dark:border-ink/20">
          {CONSENT_PURPOSES.map((purpose) => {
            const granted = byPurpose.get(purpose)?.granted ?? false;
            return (
              <ToggleRow
                key={purpose}
                label={t(`settings:privacy.consent.${purpose}`)}
                checked={granted}
                disabled={mutation.isPending}
                onChange={(checked) => mutation.mutate({ purpose, granted: checked })}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

function MeasurementsSection() {
  const { t } = useTranslation('measurements');
  const { data: settings } = useMeasurementSettings();
  const mutation = useUpdateMeasurementSettings();
  const enabled = settings?.measurementsEnabled ?? false;

  return (
    <section aria-labelledby="measurements-heading">
      <h3 id="measurements-heading" className="text-base font-medium text-ink dark:text-ink mb-1">
        {t('settings.heading')}
      </h3>
      <p className="text-sm text-ink-soft dark:text-ink-soft mb-3">{t('settings.description')}</p>
      <div className="divide-y divide-neutral-200 rounded-lg border border-husk dark:divide-neutral-700 dark:border-ink/20">
        <ToggleRow
          label={t('settings.toggle')}
          checked={enabled}
          disabled={mutation.isPending}
          onChange={(checked) => mutation.mutate(checked)}
        />
      </div>
    </section>
  );
}

function ProgressSection() {
  const { data: progress } = useProgress();
  const mutation = useUpdateProgressPreference();

  return (
    <section aria-labelledby="progress-heading">
      <h3 id="progress-heading" className="text-base font-medium text-ink dark:text-ink mb-3">
        Progress
      </h3>
      <div className="divide-y divide-neutral-200 rounded-lg border border-husk dark:divide-neutral-700 dark:border-ink/20">
        <ToggleRow
          label="Week ring &amp; milestones"
          checked={progress?.enabled ?? true}
          disabled={mutation.isPending}
          onChange={(checked) => mutation.mutate(checked)}
        />
      </div>
    </section>
  );
}

function ToggleRow({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex min-h-[44px] items-center justify-between gap-4 px-4 py-3 text-sm text-ink dark:text-ink">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.checked)}
        className="h-4 w-4 rounded border-ink/20 text-leaf focus:ring-leaf"
      />
    </label>
  );
}

function CustomFoodsSection() {
  const { t } = useTranslation('settings');
  const { data, isLoading } = useCustomFoods();
  const del = useDeleteCustomFood();
  const [editId, setEditId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showRecipeBuilder, setShowRecipeBuilder] = useState(false);

  const foods = data ?? [];
  const editFood = editId ? foods.find((f) => f.id === editId) : undefined;

  if (isLoading) return <p className="text-sm text-ink-soft">{t('customFood.loading')}</p>;

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="rounded-lg border border-leaf px-3 py-1.5 text-xs font-medium text-leaf hover:bg-leaf/5"
        >
          + {t('customFood.addFood')}
        </button>
        <button
          type="button"
          onClick={() => setShowRecipeBuilder(true)}
          className="rounded-lg border border-leaf px-3 py-1.5 text-xs font-medium text-leaf hover:bg-leaf/5"
        >
          + {t('recipe.addRecipe')}
        </button>
      </div>

      {foods.length === 0 && <p className="text-sm text-ink-soft">{t('customFood.empty')}</p>}

      <ul className="space-y-2">
        {foods.map((food) => (
          <li
            key={food.id}
            className="flex items-center justify-between rounded-xl border border-husk px-3 py-2 dark:border-stone-700"
          >
            <div>
              <p className="text-sm font-medium text-ink dark:text-stone-100">{food.name}</p>
              <p className="text-xs text-ink-soft">
                {Math.round(food.macrosPer100g.kcal)} kcal/100g
                {food.servingG ? ` · ${food.servingG}g ${t('customFood.serving')}` : ''}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEditId(food.id)}
                className="text-xs text-leaf hover:underline"
              >
                {t('customFood.edit')}
              </button>
              <button
                type="button"
                onClick={() => del.mutate(food.id)}
                className="text-xs text-red-400 hover:text-red-600"
              >
                {t('customFood.delete')}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {(showCreate || editId) && (
        <CustomFoodForm
          editFood={
            editFood
              ? {
                  id: editFood.id,
                  name: editFood.name,
                  brand: editFood.brand,
                  macrosPer100g: editFood.macrosPer100g,
                  servingG: editFood.servingG,
                }
              : undefined
          }
          onClose={() => {
            setShowCreate(false);
            setEditId(null);
          }}
        />
      )}
      {showRecipeBuilder && <RecipeBuilder onClose={() => setShowRecipeBuilder(false)} />}
    </div>
  );
}

function SavedMealsSection() {
  const { t } = useTranslation('settings');
  const { data, isLoading, isError } = useSavedMeals();
  const updateMeal = useUpdateSavedMeal();
  const deleteMeal = useDeleteSavedMeal();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  if (isLoading) {
    return (
      <p className="text-sm text-ink-soft dark:text-ink-soft">
        {t('common:loading', { ns: 'common' })}
      </p>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-clay dark:text-clay" role="alert">
        {t('settings:savedMeals.loadError')}
      </p>
    );
  }
  if (!data || data.length === 0) {
    return (
      <p className="text-sm text-ink-soft dark:text-ink-soft">{t('settings:savedMeals.empty')}</p>
    );
  }

  return (
    <ul
      className="divide-y divide-neutral-200 dark:divide-neutral-700"
      aria-label={t('settings:savedMeals.title')}
    >
      {data.map((meal) => (
        <li key={meal.id} className="flex items-center gap-3 py-3">
          {renamingId === meal.id ? (
            <form
              className="flex flex-1 gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const name = renameValue.trim();
                if (name) updateMeal.mutate({ id: meal.id, name });
                setRenamingId(null);
              }}
            >
              <input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder={t('settings:savedMeals.renamePlaceholder')}
                maxLength={120}
                className="flex-1 rounded-lg border border-ink/20 px-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-leaf dark:border-ink/20 dark:bg-shell dark:text-ink"
              />
              <button
                type="submit"
                disabled={!renameValue.trim() || updateMeal.isPending}
                className="rounded-lg bg-leaf px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                {t('settings:savedMeals.renameButton')}
              </button>
              <button
                type="button"
                onClick={() => setRenamingId(null)}
                className="text-xs text-ink-soft"
              >
                Cancel
              </button>
            </form>
          ) : (
            <>
              <button
                type="button"
                onClick={() => updateMeal.mutate({ id: meal.id, isFavorite: !meal.isFavorite })}
                aria-label={t('settings:savedMeals.favoriteAria', { name: meal.name })}
                className="shrink-0 text-base leading-none"
                title={meal.isFavorite ? 'Remove from favourites' : 'Add to favourites'}
              >
                {meal.isFavorite ? '★' : '☆'}
              </button>
              <button
                type="button"
                className="flex-1 min-w-0 text-left"
                onClick={() => {
                  setRenamingId(meal.id);
                  setRenameValue(meal.name);
                }}
                aria-label={t('settings:savedMeals.renameAria')}
              >
                <span className="block truncate font-medium text-sm text-ink dark:text-ink">
                  {meal.name}
                </span>
                <span className="block text-xs text-ink-soft dark:text-ink-soft">
                  {meal.items.length} item{meal.items.length !== 1 ? 's' : ''}
                  {meal.lastUsedAt
                    ? ` · last used ${new Date(meal.lastUsedAt).toLocaleDateString()}`
                    : ''}
                </span>
              </button>
              <button
                type="button"
                onClick={() => deleteMeal.mutate(meal.id)}
                disabled={deleteMeal.isPending}
                aria-label={t('settings:savedMeals.deleteAria', { name: meal.name })}
                className="shrink-0 rounded p-1 text-ink-soft hover:text-clay focus:outline-none focus:ring-2 focus:ring-leaf dark:hover:text-clay disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M5 1h6M2 4h12M3 4l1 10h8L13 4" />
                </svg>
              </button>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function ReportsSection() {
  const { data: entitlement } = useEntitlement();
  const report = useReportDownload();

  return (
    <section aria-labelledby="reports-heading">
      <h3 id="reports-heading" className="text-base font-medium text-ink dark:text-ink mb-1">
        Reports
      </h3>
      <p className="text-sm text-ink-soft dark:text-ink-soft mb-4">
        Generate a formatted PDF of your week or month — your adherence overview and review, ready
        to keep or share with a coach.
      </p>

      {!entitlement?.isPremium ? (
        <PremiumGate feature="reports_export" />
      ) : (
        <div className="rounded-xl border border-husk dark:border-ink/20 p-4">
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => report.start('week')}
              disabled={report.isWorking}
              className="rounded-xl bg-leaf px-4 py-2 text-sm font-semibold text-white
                         hover:bg-leaf-deep focus:outline-none focus:ring-2 focus:ring-leaf
                         disabled:opacity-60"
            >
              Weekly report (PDF)
            </button>
            <button
              type="button"
              onClick={() => report.start('month')}
              disabled={report.isWorking}
              className="rounded-xl border border-leaf px-4 py-2 text-sm font-semibold text-leaf
                         hover:bg-leaf/10 focus:outline-none focus:ring-2 focus:ring-leaf
                         disabled:opacity-60"
            >
              Monthly report (PDF)
            </button>
          </div>

          {report.isWorking && (
            <p className="mt-3 text-sm text-ink-soft dark:text-ink-soft" role="status">
              Preparing your report…
            </p>
          )}
          {report.downloadUrl && (
            <a
              href={report.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block text-sm font-semibold text-leaf underline hover:text-leaf-deep"
            >
              Download your {report.period === 'month' ? 'monthly' : 'weekly'} report →
            </a>
          )}
          {report.failed && (
            <p className="mt-3 text-sm text-clay dark:text-clay" role="alert">
              Could not prepare the report. Please try again.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function PhoneSection() {
  const { t } = useTranslation('settings');
  const { data: entitlement } = useEntitlement();
  const trialEligibility = entitlement?.trialEligibility;

  const isVerified = trialEligibility !== 'needs_phone';

  return (
    <section aria-labelledby="phone-heading">
      <h3 id="phone-heading" className="text-base font-medium text-ink dark:text-ink mb-1">
        {t('settings:phone.title')}
      </h3>
      <p className="text-sm text-ink-soft dark:text-ink-soft mb-3">
        {isVerified ? t('settings:phone.verifiedBody') : t('settings:phone.unverifiedBody')}
      </p>
      <div className="rounded-xl border border-husk dark:border-ink/20 divide-y divide-neutral-200 dark:divide-neutral-700">
        <div className="flex min-h-[44px] items-center justify-between gap-4 px-4 py-3">
          <span className="text-sm text-ink dark:text-ink">{t('settings:phone.status')}</span>
          <span
            className={`text-sm font-medium ${
              isVerified ? 'text-leaf dark:text-leaf' : 'text-ink-soft dark:text-ink-soft'
            }`}
          >
            {isVerified ? t('settings:phone.verified') : t('settings:phone.notVerified')}
          </span>
        </div>
        <div className="flex min-h-[44px] items-center justify-between gap-4 px-4 py-3">
          <span className="text-sm text-ink dark:text-ink">
            {isVerified ? t('settings:phone.change') : t('settings:phone.add')}
          </span>
          <Link
            to="/verify-phone"
            className="text-sm font-medium text-ink underline hover:text-ink dark:text-ink dark:hover:text-ink-soft"
            aria-label={isVerified ? t('settings:phone.changeAria') : t('settings:phone.addAria')}
          >
            {isVerified ? t('settings:phone.changeCta') : t('settings:phone.verifyCta')}
          </Link>
        </div>
      </div>
      {trialEligibility === 'redeemed' && (
        <p className="mt-2 text-xs text-ink-soft dark:text-ink-soft">
          {t('settings:phone.redeemed')}
        </p>
      )}
    </section>
  );
}

const MIN_PASSWORD_LENGTH = 8;

/** Validate before sending — returns an i18n error key or null. */
export function validatePasswordForm(
  newPassword: string,
  confirm: string,
): 'mismatch' | 'tooShort' | null {
  if (newPassword !== confirm) return 'mismatch';
  if (newPassword.length < MIN_PASSWORD_LENGTH) return 'tooShort';
  return null;
}

function SecuritySection() {
  const { t } = useTranslation('settings');
  const { data: hasPass, isLoading } = useQuery({
    queryKey: ['hasPassword'],
    queryFn: hasPassword,
  });
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [revokeOthers, setRevokeOthers] = useState(true);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFieldError(null);
    setSuccess(false);
    const validationError = validatePasswordForm(newPw, confirmPw);
    if (validationError) {
      setFieldError(
        validationError === 'mismatch'
          ? t('settings:security.password.mismatch')
          : t('settings:security.password.tooShort', { min: MIN_PASSWORD_LENGTH }),
      );
      return;
    }
    setSaving(true);
    try {
      const result = hasPass
        ? await changePassword({
            currentPassword: currentPw,
            newPassword: newPw,
            revokeOtherSessions: revokeOthers,
          })
        : await setPassword({ newPassword: newPw });
      if (result.error) {
        const msg = friendlyErrorMessage(result.error, t('settings:security.password.saveFailed'));
        setFieldError(
          msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('incorrect')
            ? t('settings:security.password.wrongCurrent')
            : msg,
        );
      } else {
        setSuccess(true);
        setCurrentPw('');
        setNewPw('');
        setConfirmPw('');
        void qc.invalidateQueries({ queryKey: ['hasPassword'] });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <section aria-labelledby="security-password-heading">
      <h3
        id="security-password-heading"
        className="text-base font-medium text-ink dark:text-ink mb-1"
      >
        {t('settings:security.password.sectionTitle')}
      </h3>
      <p className="text-sm text-ink-soft dark:text-ink-soft mb-3">
        {hasPass
          ? t('settings:security.password.changeBody')
          : t('settings:security.password.body')}
      </p>
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="rounded-xl border border-husk dark:border-ink/20 divide-y divide-neutral-200 dark:divide-neutral-700"
      >
        {!isLoading && hasPass && (
          <div className="px-4 py-3">
            <label htmlFor="current-password" className="block text-sm text-ink dark:text-ink mb-1">
              {t('settings:security.password.currentPassword')}
            </label>
            <input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              className="w-full rounded-lg border border-husk dark:border-ink/20 bg-transparent px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-leaf"
              required
            />
          </div>
        )}
        <div className="px-4 py-3">
          <label htmlFor="new-password" className="block text-sm text-ink dark:text-ink mb-1">
            {t('settings:security.password.newPassword')}
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            className="w-full rounded-lg border border-husk dark:border-ink/20 bg-transparent px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-leaf"
            required
          />
        </div>
        <div className="px-4 py-3">
          <label htmlFor="confirm-password" className="block text-sm text-ink dark:text-ink mb-1">
            {t('settings:security.password.confirm')}
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            className="w-full rounded-lg border border-husk dark:border-ink/20 bg-transparent px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-leaf"
            required
          />
        </div>
        {hasPass && (
          <div className="flex items-center gap-3 px-4 py-3">
            <input
              type="checkbox"
              id="revoke-others"
              checked={revokeOthers}
              onChange={(e) => setRevokeOthers(e.target.checked)}
              className="h-4 w-4 rounded border-husk accent-leaf"
            />
            <label htmlFor="revoke-others" className="text-sm text-ink dark:text-ink">
              {t('settings:security.password.revokeOthers')}
            </label>
          </div>
        )}
        {fieldError && (
          <p className="px-4 py-2 text-sm text-clay" role="alert">
            {fieldError}
          </p>
        )}
        {success && (
          <p className="px-4 py-2 text-sm text-leaf" role="status">
            {t('settings:security.password.success')}
          </p>
        )}
        <div className="px-4 py-3">
          <button
            type="submit"
            disabled={saving || isLoading}
            className="rounded-lg bg-leaf px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving
              ? t('settings:security.password.saving')
              : hasPass
                ? t('settings:security.password.changeCta')
                : t('settings:security.password.setCta')}
          </button>
        </div>
      </form>
    </section>
  );
}

export default function SettingsPage() {
  const { t } = useTranslation('settings');

  return (
    <div className="flex flex-col gap-8 max-w-lg">
      <h2 className="text-xl font-medium tracking-tight">{t('settings:title')}</h2>

      <LanguageSection />
      <PlanSection />
      <ReportsSection />
      <PhoneSection />
      <SecuritySection />
      <MeasurementsSection />
      <ProgressSection />
      <NotificationsSection />
      <PrivacySection />

      <section aria-labelledby="custom-foods-heading">
        <h3 id="custom-foods-heading" className="text-base font-medium text-ink dark:text-ink mb-1">
          {t('settings:customFood.title')}
        </h3>
        <p className="text-sm text-ink-soft dark:text-ink-soft mb-4">
          {t('settings:customFood.help')}
        </p>
        <CustomFoodsSection />
      </section>

      <section aria-labelledby="saved-meals-heading">
        <h3 id="saved-meals-heading" className="text-base font-medium text-ink dark:text-ink mb-1">
          {t('settings:savedMeals.title')}
        </h3>
        <p className="text-sm text-ink-soft dark:text-ink-soft mb-4">
          {t('settings:savedMeals.help')}
        </p>
        <SavedMealsSection />
      </section>

      <section aria-labelledby="overrides-heading">
        <h3 id="overrides-heading" className="text-base font-medium text-ink dark:text-ink mb-1">
          {t('settings:overrides.title')}
        </h3>
        <p className="text-sm text-ink-soft dark:text-ink-soft mb-4">
          {t('settings:overrides.help')}
        </p>
        <OverrideList />
      </section>
    </div>
  );
}
